'use client';

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { /* Star, */ ShoppingCart, Heart } from 'lucide-react';
import { Product, ProductVariantInventoryStatusEnum } from '@/lib/Apis';
import { formatPrice } from '@/lib/utils/format';
import { useWishlist } from '@/providers/WishlistProvider';
import { useCart } from '@/providers/CartProvider';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useBasePath } from '@/providers/BasePathProvider';
import { QuickViewModal } from './QuickViewModal';
import { useNotification } from '@/providers/NotificationProvider';
import { useAuth } from '@/providers/AuthProvider';
import { PLACEHOLDER_IMAGE_SRC } from '@/lib/constants/assets';
import { NotifyWhenInStockButton } from '@/components/NotifyWhenInStockButton';
// import { useProductReviews } from '@/hooks/useReviews';

const normalizeProductName = (name?: string) =>
  name?.trim().replace(/\s+/g, ' ').toLowerCase() ?? '';

interface ProductCardProps {
  product: Product;
  layout?: 'vertical' | 'horizontal';
  onClickProduct?: (product: Product) => void;
  onFavorite?: (product: Product) => void;
  isFavorited?: boolean;
  showFavoriteButton?: boolean;
  selectedVariantId?: string | null;
  onVariantChange?: (variantId: string | null) => void;
  defaultVariantId?: string | null;
  // Price filter props to determine pre-selection when only one variant matches
  minPrice?: number;
  maxPrice?: number;
}

export function ProductCard({
  product,
  layout = 'vertical',
  onClickProduct,
  onFavorite,
  isFavorited = false,
  showFavoriteButton = true,
  selectedVariantId: controlledVariantId,
  onVariantChange,
  defaultVariantId,
  minPrice,
  maxPrice,
  className
}: ProductCardProps & { className?: string }) {
  const router = useRouter();
  const { isAuthenticated } = useAuth();
  const { buildPath } = useBasePath();
  const [isFavorite, setIsFavorite] = useState(isFavorited);
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const { addToCart, isLoading: isAddingToCart } = useCart();
  const [isHovered, setIsHovered] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [selectedVariantImage, setSelectedVariantImage] = useState<string | null>(null);
  const [internalSelectedVariantId, setInternalSelectedVariantId] = useState<string | null>(null);

  const selectedVariantId = controlledVariantId !== undefined ? controlledVariantId : internalSelectedVariantId;

  const [showQuickView, setShowQuickView] = useState(false);
  const notification = useNotification();

  // Reviews are temporarily hidden.
  // const { reviews } = useProductReviews(product._id || product.id);
  // const liveReviewCount = reviews.length > 0 ? reviews.length : (product.summary?.reviewCount ?? 0);
  // const liveAverageRating = reviews.length > 0
  //   ? reviews.reduce((sum, r) => sum + (r.rating ?? 0), 0) / reviews.length
  //   : (product.summary?.averageRating ?? 0);
  // Handle image load state
  const handleImageLoad = useCallback(() => {
    setIsImageLoaded(true);
  }, []);

  // Handle card click
  const handleCardClick = useCallback((e: React.MouseEvent) => {
    if (onClickProduct) {
      e.preventDefault();
      onClickProduct(product);
    }
  }, [onClickProduct, product]);

  // Handle variant selection
  const handleVariantSelect = useCallback((variantId: string, variantImage: string | null) => {
    if (controlledVariantId === undefined) {
      setInternalSelectedVariantId(variantId);
    }
    if (variantImage) setSelectedVariantImage(variantImage);
    onVariantChange?.(variantId);
  }, [controlledVariantId, onVariantChange]);


  const handleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (isFavorite) {
        await removeFromWishlist(product?._id || '');
        setIsFavorite(false);
      } else {
        const added = await addToWishlist(product as unknown as Product);
        if (!added) {
          router.push(buildPath(`/login?redirect=${encodeURIComponent(typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/shop')}`));
          return;
        }
        setIsFavorite(true);
      }
      onFavorite?.(product);
    } catch (error) {
      console.error('Error updating wishlist:', error);
      notification.error(
        'Could not update wishlist',
        'We could not update your wishlist. Please try again.'
      );
    }
  };

  // Update favorite state when props or wishlist changes
  useEffect(() => {
    setIsFavorite(isInWishlist(product?._id || '') || isFavorited);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFavorited, isInWishlist, product?._id]);

  // Find the best default variant: lowest-priced in-stock variant, or lowest-priced overall if all are out of stock
  // If only one variant matches the price range (even if out of stock), pre-select it
  const bestDefaultVariant = useMemo(() => {
    if (!product.variants || product.variants.length === 0) return null;

    const min = minPrice ?? 0;
    const max = maxPrice ?? Infinity;

    // Check if there's only one variant that matches the price range
    const matchingVariants = product.variants.filter((v: any) => {
      const price = v.finalPrice ?? v.price ?? 0;
      return price >= min && price <= max;
    });

    // If only one variant matches the price range, pre-select it regardless of stock
    if (matchingVariants.length === 1) {
      return matchingVariants[0];
    }

    const inStockVariants = product.variants.filter(v => (v.inventoryCount ?? 0) > 0);
    const pool = inStockVariants.length > 0 ? inStockVariants : product.variants;

    return pool.reduce((best, current) =>
      (current.finalPrice ?? Infinity) < (best.finalPrice ?? Infinity) ? current : best
      , pool[0]);
  }, [product.variants, minPrice, maxPrice]);

  // Reset selected variant image when product changes and auto-select best variant
  useEffect(() => {
    setSelectedVariantImage(null);
    if (controlledVariantId === undefined) {
      setInternalSelectedVariantId(null);
    }
    setIsImageLoaded(false);

    if (controlledVariantId === undefined) {
      // Use explicit defaultVariantId if provided, otherwise fall back to bestDefaultVariant
      const initialId = defaultVariantId !== undefined
        ? defaultVariantId
        : ((bestDefaultVariant as any)?._id || (bestDefaultVariant as any)?.id || null);

      setInternalSelectedVariantId(initialId);
    }
  }, [product._id, defaultVariantId, bestDefaultVariant, controlledVariantId]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick(e as any);
    }
  };


  const MAX_VISIBLE_VARIANTS = 5;

  // Map all variants to a unified shape: image if available, otherwise null
  const allVariantItems = useMemo(() => {
    if (!product.variants || product.variants.length === 0) return [];
    return product.variants.map((variant) => ({
      variantId: (variant as any)._id || (variant as any).id,
      variantName: variant.name,
      image: variant.media && variant.media.length > 0 ? variant.media[0].file : null,
    }));
  }, [product.variants]);

  // Keep for backward-compat checks (add-to-cart guard, etc.)
  const variantImages = useMemo(
    () => allVariantItems.filter((v) => v.image !== null),
    [allVariantItems]
  );

  // Get selected variant
  const selectedVariant = useMemo(() => {
    if (!selectedVariantId || !product.variants) return null;
    return product.variants.find(
      (variant) => ((variant as any)._id || (variant as any).id) === selectedVariantId
    );
  }, [selectedVariantId, product.variants]);

  const displayFinalPrice = useMemo(() => {
    return selectedVariant?.finalPrice ?? bestDefaultVariant?.finalPrice ?? (product as any).finalPrice ?? (product as any).price ?? 0;
  }, [selectedVariant, bestDefaultVariant, product]);

  const displayPriceBeforeDiscount = useMemo(() => {
    return selectedVariant?.retailPrice ?? bestDefaultVariant?.retailPrice ?? (product as any).retailPrice ?? (product as any).compareAtPrice ?? 0;
  }, [selectedVariant, bestDefaultVariant, product]);

  const displayIsDiscounted = useMemo(() => {
    return selectedVariant ? selectedVariant.isDiscounted : bestDefaultVariant?.isDiscounted;
  }, [selectedVariant, bestDefaultVariant]);

  const displayDiscountPercent = useMemo(() => {
    if (!displayPriceBeforeDiscount || displayPriceBeforeDiscount === 0) return 0;
    return Math.round(((displayPriceBeforeDiscount - displayFinalPrice) / displayPriceBeforeDiscount) * 100);
  }, [displayPriceBeforeDiscount, displayFinalPrice]);

  const displayInventoryCount = useMemo(() => {
    return selectedVariant?.inventoryCount ?? bestDefaultVariant?.inventoryCount ?? product?.summary?.totalInventory ?? 0;
  }, [selectedVariant, bestDefaultVariant, product]);

  const displayInventoryStatus = useMemo(() => {
    return selectedVariant?.inventoryStatus ?? bestDefaultVariant?.inventoryStatus ?? null;
  }, [selectedVariant, bestDefaultVariant]);



  // ... (inside component)

  const imageSource = useMemo(() => {
    const src = selectedVariantImage || product.media?.[0]?.file || PLACEHOLDER_IMAGE_SRC;
    return {
      src,
      alt: product.name || 'Product image'
    };
  }, [product.media, product.name, selectedVariantImage]);

  // Reset image loaded state when image source changes
  useEffect(() => {
    setIsImageLoaded(false);
  }, [imageSource.src]);



  const stripHtml = (html: string) =>
    html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

  const outOfStockVariantId: string | undefined =
    selectedVariantId ||
    (product.variants?.[0] as any)?._id ||
    (product.variants?.[0] as any)?.id;

  const handleAddToCart = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const hasMultipleVariants = (product.variants?.length || 0) > 1;
    if (!selectedVariantId && hasMultipleVariants && variantImages.length > 1) {
      notification.error(
        'Select a variant',
        'Please choose a variant before adding this item to your cart.'
      );
      return;
    }

    try {
      const firstVariantId = product.variants && product.variants.length > 0
        ? ((product.variants[0] as any)._id || (product.variants[0] as any).id)
        : null;
      const finalVariantId = selectedVariantId || firstVariantId;
      await addToCart(product._id || product.id, 1, finalVariantId || undefined);
    } catch (error) {
      console.error('Failed to add to cart', error);
    }
  };

  if (layout === 'horizontal') {
    return (
      <>
        <motion.div
          className={`group flex min-h-[148px] h-full cursor-pointer overflow-hidden rounded-2xl border border-slate-100 bg-white transition-all hover:border-hsecondary/15 hover:shadow-sm ${className ?? ''}`}
          whileHover={{ y: -1 }}
          aria-labelledby={`product-${product.id}-title`}
          role="article"
          tabIndex={0}
          onClick={handleCardClick}
          onKeyDown={handleKeyDown}
        >
          <div className="relative m-3 mr-0 w-28 shrink-0 self-stretch overflow-hidden rounded-xl bg-slate-50 sm:w-32">
            <Image
              src={imageSource.src}
              alt={imageSource.alt}
              className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-105"
              height={180}
              width={180}
              priority={false}
              onLoad={handleImageLoad}
              onError={() => setIsImageLoaded(true)}
              key={imageSource.src}
            />

            {product.showStock && displayInventoryCount === 0 ? (
              <span className="absolute left-2 top-2 z-10 rounded-md bg-gray-800 px-2 py-0.5 text-[9px] font-semibold text-white">
                Out of Stock
              </span>
            ) : product.showStock && displayInventoryStatus === ProductVariantInventoryStatusEnum.LOWSTOCK ? (
              <span className="absolute left-2 top-2 z-10 rounded-md bg-amber-500 px-2 py-0.5 text-[9px] font-semibold text-white">
                Low Stock
              </span>
            ) : displayIsDiscounted ? (
              <span className="absolute left-2 top-2 z-10 rounded-full bg-haccent-500 px-2 py-0.5 text-[9px] font-bold text-white">
                Save {displayDiscountPercent}%
              </span>
            ) : (product as any).isNew ? (
              <span className="absolute left-2 top-2 z-10 rounded-md bg-green-700 px-2 py-0.5 text-[9px] font-semibold text-white">
                New
              </span>
            ) : null}

            {showFavoriteButton && (
              <button
                type="button"
                onClick={handleFavorite}
                className="absolute right-2 top-2 z-10 rounded-full bg-white/90 p-1.5 shadow-sm transition hover:bg-white"
                aria-label={isFavorite ? 'Remove from wishlist' : 'Add to wishlist'}
              >
                <Heart className={`size-3.5 transition-colors ${isFavorite ? 'fill-red-400 text-red-400' : 'text-gray-400'}`} />
              </button>
            )}
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-1.5 p-3.5 sm:p-4">
            <div className="min-w-0">
              <h3
                id={`product-${product.id}-title`}
                className="line-clamp-2 font-serif text-[17px] font-medium italic leading-snug text-hsecondary"
              >
                {product.name}
              </h3>

              {product.variants?.length === 1 &&
                product.variants[0].name &&
                normalizeProductName(product.variants[0].name) !== normalizeProductName(product.name) && (
                <span className="mt-0.5 block truncate text-[11px] text-gray-400">
                  {product.variants[0].name}
                </span>
              )}

              {product.description && (
                <p className="mt-1 line-clamp-1 text-xs leading-relaxed text-gray-400">
                  {stripHtml(product.description)}
                </p>
              )}
            </div>

            {product.variants && product.variants.length > 1 && (
              <div className="flex min-w-0 items-center gap-1.5 pt-0.5" onClick={(event) => event.stopPropagation()}>
                {allVariantItems.slice(0, 4).map(({ variantId, variantName, image }) => (
                  <button
                    key={variantId}
                    type="button"
                    onClick={() => handleVariantSelect(variantId, image)}
                    className={`size-6 shrink-0 overflow-hidden rounded-full border-2 transition-all ${selectedVariantId === variantId ? 'scale-105 border-hsecondary' : 'border-gray-200 hover:border-gray-400'}`}
                    aria-label={variantName}
                    title={variantName}
                  >
                    <Image
                      src={image || product.media?.[0]?.file || PLACEHOLDER_IMAGE_SRC}
                      alt={variantName || ''}
                      width={24}
                      height={24}
                      className="h-full w-full object-cover"
                    />
                  </button>
                ))}
                {allVariantItems.length > 4 && (
                  <span className="truncate text-[10px] text-gray-400">+{allVariantItems.length - 4} more</span>
                )}
              </div>
            )}

            <div className="mt-auto flex items-end justify-between gap-3" onClick={(event) => event.stopPropagation()}>
              <div className="flex min-w-0 flex-wrap items-baseline gap-1.5">
                <span className="font-serif text-[17px] font-semibold italic text-hsecondary">
                  ${(displayFinalPrice || (product as any).price || 0).toFixed(2)}
                </span>
                {displayIsDiscounted && displayPriceBeforeDiscount > 0 && (
                  <span className="text-[11px] text-gray-400 line-through">
                    ${(displayPriceBeforeDiscount || 0).toFixed(2)}
                  </span>
                )}
              </div>

              {displayInventoryCount === 0 ? (
                outOfStockVariantId ? (
                  <NotifyWhenInStockButton
                    productId={product._id || product.id}
                    variantId={outOfStockVariantId}
                    size="sm"
                    appearance="compact"
                  />
                ) : null
              ) : (
                <button
                  type="button"
                  onClick={handleAddToCart}
                  disabled={isAddingToCart || ((product.variants?.length || 0) > 1 && variantImages.length > 1 && !selectedVariantId)}
                  className="flex shrink-0 items-center justify-center gap-1.5 rounded-full bg-hsecondary px-4 py-2 text-xs font-medium text-white transition hover:bg-hsecondary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ShoppingCart className="size-3.5" />
                  <span className="hidden sm:inline">Add to cart</span>
                  <span className="sm:hidden">Add</span>
                </button>
              )}
            </div>
          </div>
        </motion.div>

        {showQuickView && (
          <QuickViewModal
            product={product}
            onClose={() => setShowQuickView(false)}
            onNavigateToProduct={() => handleCardClick}
          />
        )}
      </>
    );
  }

  return (
    <>
      <motion.div
        className={`bg-white rounded-2xl overflow-hidden group h-full flex flex-col cursor-pointer hover:-translate-y-1 hover:shadow-md transition-all relative ${className ?? ''}`}
        whileHover={{ y: -2 }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        aria-labelledby={`product-${product.id}-title`}
        role="article"
        tabIndex={0}
        onClick={handleCardClick}
        onKeyDown={handleKeyDown}
      >
        {/* Image */}
        <div className="relative aspect-square overflow-hidden bg-white shrink-0">
          <Image
            src={imageSource.src}
            alt={imageSource.alt}
            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
            height={400}
            width={400}
            priority={false}
            onLoad={handleImageLoad}
            onError={() => setIsImageLoaded(true)}
            key={imageSource.src}
          />

          {/* Top-left badge: out of stock takes priority (only if showStock), then discount, then new */}
          {product.showStock && displayInventoryCount === 0 ? (
            <span className="absolute top-2.5 left-2.5 bg-gray-800 text-white text-[10px] font-semibold px-2.5 py-1 rounded-md z-10">
              Out of Stock
            </span>
          ) : product.showStock && displayInventoryStatus === ProductVariantInventoryStatusEnum.LOWSTOCK ? (
            <span className="absolute top-2.5 left-2.5 bg-amber-500 text-white text-[10px] font-semibold px-2.5 py-1 rounded-md z-10">
              Low Stock
            </span>
          ) : displayIsDiscounted ? (
            <span className="absolute top-2.5 left-2.5 bg-haccent-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full z-10">
              Save {displayDiscountPercent}%
            </span>
          ) : (product as any).isNew ? (
            <span className="absolute top-2.5 left-2.5 bg-green-700 text-white text-[10px] font-semibold px-2.5 py-1 rounded-md z-10">
              New
            </span>
          ) : null}

          {/* Wishlist button — always visible */}
          {showFavoriteButton && (
            <button
              type="button"
              onClick={handleFavorite}
              className="absolute top-2.5 right-2.5 p-1.5 rounded-full bg-white/90 hover:bg-white shadow-sm transition-all duration-200 z-10"
              aria-label={isFavorite ? 'Remove from wishlist' : 'Add to wishlist'}
            >
              <Heart className={`size-4 transition-colors ${isFavorite ? 'fill-red-400 text-red-400' : 'text-gray-400'}`} />
            </button>
          )}
        </div>

        {/* Info */}
        <div className="p-3 pb-3 flex flex-col gap-1 flex-1">
          {/* Reviews are temporarily hidden.
          <div className="flex items-center gap-0.5">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`size-3 ${i < Math.floor(liveAverageRating)
                  ? 'fill-hsecondary text-hsecondary'
                  : 'fill-gray-200 text-gray-200'
                  }`}
              />
            ))}
            <span className="text-[10px] text-gray-400 ml-1">({liveReviewCount})</span>
          </div>
          */}

          {/* Name */}
          <h3 className="text-sm font-semibold text-gray-800 line-clamp-2 leading-snug">
            {product.name}
          </h3>

          {/* Single variant name — shown when there's exactly one variant (no pills rendered) */}
          {product.variants?.length === 1 &&
            product.variants[0].name &&
            normalizeProductName(product.variants[0].name) !== normalizeProductName(product.name) && (
            <span className="text-[11px] text-gray-400 leading-none -mt-0.5">
              {product.variants[0].name}
            </span>
          )}

          {/* Description */}
          {product.description && (
            <p className="text-xs text-gray-400 line-clamp-2 leading-relaxed">
              {stripHtml(product.description)}
            </p>
          )}

          {/* Variant Selector */}
          {product.variants && product.variants.length > 1 && (
            <div className="flex flex-col gap-1 pt-1" onClick={e => e.stopPropagation()}>
              <div className="flex flex-wrap gap-1">
                {allVariantItems.slice(0, MAX_VISIBLE_VARIANTS).map(({ variantId, variantName, image }) => (
                  <button
                    key={variantId}
                    type="button"
                    onClick={() => handleVariantSelect(variantId, image)}
                    className={`w-6 h-6 rounded-full border-2 overflow-hidden transition-all shrink-0 ${selectedVariantId === variantId
                      ? 'border-hsecondary scale-110'
                      : 'border-gray-200 hover:border-gray-400'
                      }`}
                    aria-label={variantName}
                    title={variantName}
                  >
                    {/* Fall back to the product image when the variant has no photo of its own */}
                    <Image src={image || product.media?.[0]?.file || PLACEHOLDER_IMAGE_SRC} alt={variantName || ''} width={24} height={24} className="w-full h-full object-cover" />
                  </button>
                ))}
                {allVariantItems.length > MAX_VISIBLE_VARIANTS && (
                  <span className="text-[10px] text-gray-400 self-center shrink-0">
                    +{allVariantItems.length - MAX_VISIBLE_VARIANTS} more
                  </span>
                )}
              </div>
              {selectedVariant?.name &&
                normalizeProductName(selectedVariant.name) !== normalizeProductName(product.name) && (
                <span className="text-[10px] text-gray-500 leading-none">
                  {selectedVariant.name}
                </span>
              )}
            </div>
          )}

          {/* Price */}
          <div className="flex items-center gap-2 pt-0.5">
            <span className="font-bold text-sm text-gray-900">
              ${(displayFinalPrice || (product as any).price || 0).toFixed(2)}
            </span>
            {displayIsDiscounted && displayPriceBeforeDiscount > 0 && (
              <span className="text-sm text-gray-400 line-through">
                ${(displayPriceBeforeDiscount || 0).toFixed(2)}
              </span>
            )}
          </div>

          {/* Add to Cart / Notify — always visible at bottom */}
          <div className="mt-auto pt-2" onClick={e => e.stopPropagation()}>
            {displayInventoryCount === 0 ? (
              (() => {
                const outOfStockVariantId: string | undefined =
                  selectedVariantId ||
                  (product.variants?.[0] as any)?._id ||
                  (product.variants?.[0] as any)?.id;
                return outOfStockVariantId ? (
                  <NotifyWhenInStockButton
                    productId={product._id || product.id}
                    variantId={outOfStockVariantId}
                    size="sm"
                  />
                ) : null;
              })()
            ) : (
              <button
                type="button"
                onClick={handleAddToCart}
                disabled={isAddingToCart || ((product.variants?.length || 0) > 1 && variantImages.length > 1 && !selectedVariantId)}
                className="w-full bg-hsecondary hover:bg-hsecondary/90 text-white text-[10px] min-[380px]:text-[11px] sm:text-xs font-medium px-2 sm:px-5 py-2 rounded-full flex items-center justify-center gap-1 whitespace-nowrap transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ShoppingCart className="size-3.5 shrink-0" />
                <span>Add to cart</span>
              </button>
            )}
          </div>
        </div>
      </motion.div>

      {showQuickView && (
        <QuickViewModal
          product={product}
          onClose={() => setShowQuickView(false)}
          onNavigateToProduct={() => handleCardClick}
        />
      )}
    </>
  );
}
